import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const useNavbarInteraction = () => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleNavigation = (path: string) => {
    window.scrollTo(0, 0);
    navigate(path);
    setActiveMenu(null);
    setActiveCategory(null);
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > 0);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  const updateDropdownPosition = (btn: HTMLButtonElement | null) => {
    const container = dropdownRef.current;
    const scroller = scrollRef.current;
    if (!btn || !container || !scroller) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    const left = btnRect.left - containerRect.left + scroller.scrollLeft;
    const maxLeft = Math.max(8, container.clientWidth - 24);
    const finalLeft = Math.max(8, Math.min(left, maxLeft));

    setDropdownLeft(finalLeft);
  };

  const handleMenuEnter = (menu: string) => {
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    setActiveMenu(null);
    setActiveCategory(null);
    setDropdownLeft(null);
  };

  const handleCategoryEnter = (category: string) => {
    setActiveCategory(category);
  };

  const handleCategoryLeave = () => {
    setActiveCategory(null);
  };

  const handleSubmenuLeave = () => {
    setActiveMenu(null);
    setActiveCategory(null);
    setDropdownLeft(null);
  };

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -240 : 240,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    const onScroll = () => {
      checkScroll();
    };

    const onResize = () => {
      checkScroll();
    };

    el.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
        setActiveCategory(null);
        setDropdownLeft(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return {
    dropdownRef,
    scrollRef,
    activeMenu,
    activeCategory,
    showLeft,
    showRight,
    dropdownLeft,
    handleNavigation,
    handleMenuEnter,
    handleMenuLeave,
    handleCategoryEnter,
    handleCategoryLeave,
    handleSubmenuLeave,
    setHoveredCategory: setActiveCategory,
    updateDropdownPosition,
    scroll,
  };
};
